import React, { useEffect, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import { Badge, Rate } from "antd";
import { NavLink, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import Table from "../../Common/Table";
import { Image } from "antd";
import ReviewActions from "./actions";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import { HomeOutlined } from "@ant-design/icons";
import { ExpandableText } from "../../Common/ExpandableText";
import { ReviewTableRow, useReviewTableGet } from "../../../hooks/api/reviews";
import { ReviewStatus } from "../../../enums/reviews";
import { RequestData, RequestOptionsType } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import { queryFilterParams, getSorterParams } from "../../../utils";
import dayjs from "dayjs";
import { dateTimeFormat } from "../../../contstant";

export const badgeColors: Record<ReviewStatus, string> = {
  [ReviewStatus.pending]: "gold",
  [ReviewStatus.approved]: "green",
  [ReviewStatus.rejected]: "red",
};

export const ReviewStatusLabels = () => {
  const { t } = useTranslation();

  return {
    [ReviewStatus.pending]: t("pages.reviews.status.pending"),
    [ReviewStatus.approved]: t("pages.reviews.status.approved"),
    [ReviewStatus.rejected]: t("pages.reviews.status.rejected"),
  } as Record<ReviewStatus, string>;
};

let entityCache: RequestOptionsType[] | null = null;
let userCache: RequestOptionsType[] | null = null;

export default function ReviewsContent(): React.ReactNode | null {
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const statusLabels = ReviewStatusLabels();
  const { setBreadcrumbs } = useBreadcrumbs();
  const reviewTableGet = useReviewTableGet();

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: t("routes.reviews") },
    ]);
  }, [i18n.language, t]);

  const loadEntityOptions = async (
    reviewTableGet: ReturnType<typeof useReviewTableGet>,
  ): Promise<RequestOptionsType[]> => {
    if (entityCache) {
      return entityCache;
    }

    const response = await reviewTableGet.fetch({
      page: 1,
      pageSize: 50,
    });

    if (!response?.data) return [];

    const map = new Map<string, RequestOptionsType>();

    response.data.forEach((item) => {
      const entity = item.polymorphicRelation?.entityName;
      if (entity) {
        map.set(entity, {
          label: entity,
          value: entity,
        });
      }
    });

    entityCache = Array.from(map.values());
    return entityCache;
  };

  const loadUserOptions = async (
    reviewTableGet: ReturnType<typeof useReviewTableGet>,
  ): Promise<RequestOptionsType[]> => {
    if (userCache) return userCache;

    const response = await reviewTableGet.fetch({
      page: 1,
      pageSize: 50,
    });

    if (!response?.data) return [];

    const map = new Map<string, RequestOptionsType>();

    response.data.forEach((item) => {
      const user = item.user;
      if (user?.id) {
        map.set(user.id, {
          label: user.fullName,
          value: user.id,
        });
      }
    });

    userCache = Array.from(map.values());
    return userCache;
  };

  const columns: ProColumns<ReviewTableRow>[] = [
    {
      title: "User",
      dataIndex: "userId",
      key: "user",
      sorter: true,
      valueType: "select",
      request: () => loadUserOptions(reviewTableGet),
      fieldProps: {
        showSearch: true,
        filterOption: true,
      },
      search: {
        transform: (value) => ({
          userId: value,
        }),
      },
      render: (_, record) => <NavLink to="/">{record.user.fullName}</NavLink>,
    },
    {
      title: t("pages.reviews.table.review"),
      dataIndex: "review",
      key: "review",
      hideInSearch: true,
      render: (_, record) => {
        return (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              marginBottom: 10,
            }}
          >
            <ExpandableText text={record.text} />
            {record.imageUrls && record.imageUrls.length > 0 && (
              <Image.PreviewGroup>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    flexWrap: "wrap",
                    marginTop: "4px",
                  }}
                >
                  {record.imageUrls.map((imgUrl: string, index: number) => (
                    <Image
                      key={index}
                      width={56}
                      height={56}
                      src={imgUrl}
                      alt={`review-img-${index}`}
                      style={{ objectFit: "cover", borderRadius: "4px" }}
                    />
                  ))}
                </div>
              </Image.PreviewGroup>
            )}
            <NavLink to="/">{t("pages.reviews.exampleRecipe")}</NavLink>
          </div>
        );
      },
    },
    {
      title: t("pages.reviews.table.rating"),
      dataIndex: "rating",
      key: "rating",
      valueType: "select",
      valueEnum: {
        1: "1",
        2: "2",
        3: "3",
        4: "4",
        5: "5",
      },
      width: 185,
      render: (_, record) => <Rate disabled defaultValue={record.rating} />,
    },
    {
      title: t("pages.reviews.table.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      hideInSearch: true,
      width: 160,
      renderText: (text) => dayjs(text).format(dateTimeFormat),
    },
    {
      title: t("pages.reviews.table.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      valueType: "dateRange",
      hideInTable: true,
      search: {
        transform: (value?: [string, string]) => {
          if (!value || value.length !== 2) return {};

          return {
            createdFrom: value[0],
            createdTo: value[1],
          };
        },
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      sorter: true,
      valueType: "select",
      valueEnum: statusLabels,
      render: (_, record: { status: ReviewStatus }) => (
        <Badge
          color={badgeColors[record.status]}
          text={statusLabels[record.status]}
        />
      ),
      width: 120,
    },
    {
      title: t("pages.reviews.table.images"),
      dataIndex: "images",
      key: "images",
      valueType: "select",
      hideInTable: true,
    },
    {
      title: t("pages.reviews.table.entity"),
      dataIndex: "entity",
      key: "entity",
      valueType: "select",
      hideInTable: true,
      request: () => loadEntityOptions(reviewTableGet),
      fieldProps: {
        showSearch: true,
        filterOption: true,
      },
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<ReviewTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : "1",
      pageSize: pageSize ? `${pageSize}` : "10",
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return reviewTableGet
      .fetch({
        ...newParams,
        // name: newParams?.name || undefined,
      })
      .then((response) => {
        if (response) {
          const { data, total } = response;

          return {
            data: (data || []).map((item) => ({
              ...item,
              key: item.id,
            })),
            success: true,
            total,
          };
        }

        return { data: [], success: false, total: 0 };
      });
  };

  return (
    <>
      <Table<ReviewTableRow>
        key={`${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        className={clsx("transparent")}
        headerTitle={t("pages.reviews.reviewsList")}
        toolBarRender={() => []}
        request={tableRequest}
        // dataSource={reviews}
        columns={columns}
        actions={[
          [
            ReviewActions,
            {
              onSuccess: () => setTableKey(Date.now()),
            },
          ],
        ]}
        // rowClassName="cursor-pointer"
        search={{
          style: {
            // display: 'none',
          },
        }}
        options={{
          search: {
            name: "name",
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-reviews",
          persistenceType: "localStorage",
        }}
      />
    </>
  );
}
